import React, { Fragment } from 'react';
import {
  QUIZ_HEADER_LABEL_DEFAULT_SIZE,
  QUIZ_HEADER_NODE_DEFAULT_SIZE,
  QUIZ_HEADER_SEGMENT_GAP_DEFAULT,
  QUIZ_SECTION_STEPS_DEFAULT_THICKNESS,
  type ResolvedQuizHeader,
} from './quizHeader';
import type { VisitorSectionInput } from './sectionProgress';
import { sectionBarsForVisitor, sectionStepsForVisitor } from './sectionSteps';

/**
 * Шаги-разделы шапки воронки (`settings.quizHeader.progress:
 * 'section-nodes'`, слово владельца 21.10.2026, Blinkist): строка кружков —
 * узел на раздел, — соединённых линиями толщиной `progressThickness` (нет —
 * 3 px). Пройденные и текущий узлы и линия ДО текущего — цветом заливки шапки
 * (`progressColor` / начало градиента / прежний цвет бренда), прочие — цветом
 * дорожки. Под узлом текущего раздела — его подпись (кегль
 * `progressLabelFontSize`, нет — 14; цвет текста темы): по центру узла, у
 * первого узла — от левого края, у последнего — до правого, не шире шапки.
 *
 * Конечный узел «финиш» (`progressEndNode`, задание
 * stylepack/section-steps-end, Blinkist: три раздела — четыре узла) — после
 * последнего раздела, заполняется, когда пройден последний раздел; с ним
 * последний раздел не крайний — подпись по центру его узла. Отступ справа
 * (`progressInsetRight`) — поле строки шагов от правого края строки шапки.
 * Нет полей — разметка прежняя.
 *
 * Полоса по разделам (`progress: 'section-bars'`, признак `sectionBars`,
 * задание stylepack/section-bars, Lovi) — тот же модуль, вместо узлов строка
 * отрезков: отрезок на раздел шириной по числу экранов раздела (или по своим
 * пропорциям `segmentWeights`), зазор `segmentGap`, толщина, скругление,
 * дорожка и заливка — как у полосы `bar`. Отступ справа и место в строке
 * кнопок — те же, что у шагов.
 *
 * Узлы пака `chrome.progress: 'section-nodes'` (`SectionNodesProgress.tsx`)
 * — другой вид и этим модулем не рисуются; общий у них только счёт.
 */
type HeaderLook = Pick<
  ResolvedQuizHeader,
  | 'fillColor'
  | 'trackColor'
  | 'thickness'
  | 'nodeSize'
  | 'labelFontSize'
  | 'endNode'
  | 'insetRight'
> &
  Partial<
    Pick<
      ResolvedQuizHeader,
      'sectionBars' | 'segmentGap' | 'segmentWeights' | 'fillBackground'
    >
  >;

/**
 * Вид отрезков без полей — как у полосы `bar` плеера: толщина 5, заливка
 * градиентом темы (нет — бренд), прежняя дорожка, зазор 4.
 */
export function sectionBarsLook(
  header: Pick<
    HeaderLook,
    'thickness' | 'fillBackground' | 'trackColor' | 'segmentGap'
  >,
) {
  return {
    thick: header.thickness ?? 5,
    fill:
      header.fillBackground ??
      'var(--gradient-progress, var(--color-primary, #1677ff))',
    track: header.trackColor ?? 'var(--color-border-primary, #f0f0f0)',
    gap: header.segmentGap ?? QUIZ_HEADER_SEGMENT_GAP_DEFAULT,
  };
}

/** Строка отрезков полосы по разделам. */
function QuizSectionBars({
  header,
  input,
  flush,
}: {
  header: HeaderLook;
  input: VisitorSectionInput;
  flush: boolean;
}) {
  const bars = sectionBarsForVisitor(input, { weights: header.segmentWeights });
  const { thick, fill, track, gap } = sectionBarsLook(header);
  return (
    <div
      data-testid="quiz-section-bars"
      style={{
        display: 'flex',
        gap: `${gap}px`,
        marginTop: flush ? 0 : 8,
        ...(header.insetRight !== undefined
          ? { marginRight: header.insetRight }
          : {}),
      }}
    >
      {bars.map((b, k) => (
        <span
          key={k}
          data-testid={`quiz-section-bar-${k}`}
          style={{
            flex: `${b.weight} 1 0`,
            minWidth: 0,
            height: thick,
            borderRadius: 999,
            background: track,
            overflow: 'hidden',
          }}
        >
          <span
            data-testid={`quiz-section-bar-fill-${k}`}
            style={{
              display: 'block',
              width: `${Math.round(b.fill * 10000) / 100}%`,
              height: '100%',
              borderRadius: 999,
              background: fill,
              transition: 'width var(--transition-ms, 0ms) ease',
            }}
          />
        </span>
      ))}
    </div>
  );
}

/** Вид шагов: свои поля шапки, без них — прежние величины и цвета. */
export function sectionStepsLook(header: HeaderLook) {
  return {
    size: header.nodeSize ?? QUIZ_HEADER_NODE_DEFAULT_SIZE,
    thick: header.thickness ?? QUIZ_SECTION_STEPS_DEFAULT_THICKNESS,
    fill: header.fillColor ?? 'var(--color-primary, #1677ff)',
    track: header.trackColor ?? 'var(--color-border-primary, #f0f0f0)',
    fontSize: header.labelFontSize ?? QUIZ_HEADER_LABEL_DEFAULT_SIZE,
    text: 'var(--color-text-primary, #1a1a1a)',
  };
}

export function QuizSectionStepsProgress({
  header,
  input,
  flush,
}: {
  header: HeaderLook;
  input: VisitorSectionInput;
  /** Полоса в строке кнопок (`layout: 'one-row'`) — без отступа сверху. */
  flush: boolean;
}) {
  if (header.sectionBars === true) {
    return <QuizSectionBars header={header} input={input} flush={flush} />;
  }
  const state = sectionStepsForVisitor(input, { endNode: header.endNode });
  const { size, thick, fill, track, fontSize, text } = sectionStepsLook(header);
  const last = state.nodes.length - 1;
  return (
    <div
      data-testid="quiz-section-steps"
      style={{
        marginTop: flush ? 0 : 8,
        ...(header.insetRight !== undefined
          ? { marginRight: header.insetRight }
          : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {state.nodes.map((s, k) => (
          <Fragment key={k}>
            <span
              data-testid={`quiz-section-step-${k}`}
              data-state={s}
              data-end={
                header.endNode === true && k === last ? 'true' : undefined
              }
              style={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                flexShrink: 0,
                background: s === 'idle' ? track : fill,
              }}
            />
            {k < last ? (
              <span
                data-testid={`quiz-section-step-line-${k}`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: thick,
                  // Линия к пройденному или текущему узлу — заливкой (без
                  // финиша это ровно «до текущего»).
                  background: state.nodes[k + 1] !== 'idle' ? fill : track,
                }}
              />
            ) : null}
          </Fragment>
        ))}
      </div>
      {state.label !== undefined ? (
        <div
          style={{
            position: 'relative',
            height: Math.ceil(fontSize * 1.3),
            marginTop: 6,
          }}
        >
          <span
            data-testid="quiz-section-step-label"
            data-node={state.current}
            style={{
              position: 'absolute',
              top: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize,
              lineHeight: 1.3,
              color: text,
              ...labelPlace(state.current, last, size),
            }}
          >
            {state.label}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Место подписи узла `k` из `last + 1`: первый (и единственный) — от левого
 * края, последний — до правого (обе не шире шапки), прочие — центром на
 * центре узла. Центр узла
 * `k` в строке с равными линиями — `size/2 + k/last · (100% − size)`.
 */
function labelPlace(
  k: number,
  last: number,
  size: number,
): React.CSSProperties {
  if (k <= 0 || last <= 0) return { left: 0, textAlign: 'left' };
  if (k >= last) return { right: 0, textAlign: 'right' };
  const frac = k / last;
  const pct = Math.round(frac * 10000) / 100;
  const shift = Math.round(size * frac * 100) / 100;
  const center = `${size / 2}px + ${pct}% - ${shift}px`;
  return {
    left: `calc(${center})`,
    transform: 'translateX(-50%)',
    textAlign: 'center',
    // По центру узла — не шире удвоенного расстояния до ближнего края:
    // длинная подпись обрезается многоточием, а не вылезает за шапку.
    maxWidth: `calc(2 * min(${center}, 100% - (${center})))`,
  };
}
